import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DoctorDetails from './DoctorDetails';
import Loader from './Loader';
import PersonalDetails from './PersonalDetails';
import { toast } from 'react-toastify';
import { registerPatient, bookSlot, fetchPatientByPhone, updatePaymentStatus, fetchMasterLists, createRazorPayOrder, createHDFCSession } from '../Services/api';
import { useRazorpay } from './Razorpay';
import { initiateHDFCPayment } from './HDFCGateway';

import PatientOpCard from "./PatientOpCard";


const initialFormData = {
  salutation: '',
  firstName: '',
  middleName: '',
  lastName: '',
  dob: '',
  age: '',
  gender: '',
  mobileNo: '',
  email: '',
  maritalStatus: '',
  address: '',
  city: '',
  city_id: '',
  district: '',
  district_id: '0',
  state: '',
  state_id: '',
  country: '',
  country_id: '',
  pincode: '',
  patientId: '',
  selectedDoctorName: '',
  visitType: "walkin",
  appointmentType: "in_person",
  consultationFee: "",

};

const PatientForm = ({ onSubmitSuccess, prefillData, onBookingSuccess, onPatientRegistered }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [showOpCard, setShowOpCard] = useState(false);
  const [opCardPatient, setOpCardPatient] = useState(null);
  const [redirectAfterOpCard, setRedirectAfterOpCard] = useState(false);
  const [newPatientRegistered, setNewPatientRegistered] = useState(false);
  const logo = process.env.REACT_APP_HOSPITAL_LOGO;
  const hospitalName = process.env.REACT_APP_PAY_HOSPITAL_NAME;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { loadAndOpenRazorpay } = useRazorpay();
  const payGatewayEnabled = process.env.REACT_APP_PAY_GATEWAY_ENABLE === "true";
  const [loadingMessage, setLoadingMessage] = useState("");

  useEffect(() => {
    if (formData.selectedDoctor) {
      document.body.style.paddingBottom = "260px";
    } else {
      document.body.style.paddingBottom = "";
    }
    return () => { document.body.style.paddingBottom = ""; };
  }, [formData.selectedDoctor]);

  const [masterLists, setMasterLists] = useState(() => {
    try {
      const raw = localStorage.getItem("masterLists");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const calculateSlotWindow = (startTime, endTime) => {
  if (!startTime || !endTime) return "0";

  const [sh, sm] = startTime.split(":").map(Number);
  const [eh, em] = endTime.split(":").map(Number);

  const startMinutes = sh * 60 + sm;
  const endMinutes = eh * 60 + em;

  return String(Math.max(0, endMinutes - startMinutes));
};


  // Load master lists
  useEffect(() => {
    if (!masterLists) {
      loadMasterLists();
    }
  }, []);

  const loadMasterLists = async () => {
    try {
      setLoadingMessage("Loading master data...");
      setLoading(true);

      const res = await fetchMasterLists();
      if (res) {
        setMasterLists(res);
        localStorage.setItem("masterLists", JSON.stringify(res));
      }

    } catch (err) {
      console.error("Failed to load master lists:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (prefillData && typeof prefillData === 'object') {
      const norm = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '');



      const localAddr = prefillData.localAddress || {};

      const resolvedCountry = masterLists?.countries?.find(
        (c) => Number(c.country_id) === Number(localAddr.country)
      );

      const resolvedState = masterLists?.states?.find(
        (s) => Number(s.state_id) === Number(localAddr.state)
      );

      const resolvedCity = masterLists?.cities?.find(
        (c) => Number(c.city_id) === Number(localAddr.city)
      );

      const resolvedDistrict = masterLists?.districts?.find(
        (d) => Number(d.district_id) === Number(localAddr.district)
      );

      setFormData((prev) => ({
        ...prev,
        salutation: prefillData.salutation ?? prev.salutation,
        firstName: prefillData.firstName ?? prev.firstName,
        middleName: prefillData.middleName ?? prev.middleName,
        lastName: prefillData.lastName ?? prev.lastName,
        dob: prefillData.dob ?? prev.dob,
        age: prefillData.age !== undefined && prefillData.age !== null ? String(prefillData.age) : prev.age,
        gender: prefillData.gender ?? prev.gender,
        mobileNo: prefillData.phoneNumber ?? prev.mobileNo,
        email: prefillData.email ?? prev.email,
        address: prefillData.address ?? prev.address,
        address1: localAddr.address1 ?? prev.address1,
        address2: localAddr.address2 ?? prev.address2,
        address3: localAddr.address3 ?? prev.address3,
        city: localAddr.area ?? prev.city,
        city_id: String(resolvedCity?.city_id || prefillData.city_id || prev.city_id),
        district: resolvedDistrict?.district_name ?? prev.district,
        district_id: resolvedDistrict?.district_id ?? prev.district_id,
        state: resolvedDistrict?.state_name ?? prev.state,
        state_id: resolvedState?.state_id ?? prev.state_id,
        country: resolvedDistrict?.country_name ?? prev.country,
        country_id: resolvedCountry?.country_id ?? prev.country_id,
        pincode: localAddr?.pin ?? prev.pincode,
        maritalStatus: prefillData.marital_status ?? prev.maritalStatus,
        patientId: prefillData.patientId ? String(prefillData.patientId) : '',
      }));
    }
  }, [prefillData, masterLists]);


  const calculateAge = (dobStr) => {
    if (!dobStr) return '';
    const dob = new Date(dobStr);
    if (Number.isNaN(dob.getTime())) return '';
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const monthDiff = now.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
      age--;
    }
    return age >= 0 ? String(age) : '';
  };
  const verifiedPhoneNumber = localStorage.getItem("phoneNumber");
  const salutationToGender = {
    'Mr.': 'Male',
    'Master.': 'Male',
    'Baby Boy of.': 'Male',
    'Brother of.': 'Male',
    'S/o.': 'Male',
    'B/O.': 'Male',
    'Mrs.': 'Female',
    'Ms.': 'Female',
    'Miss.': 'Female',
    'Baby Girl of.': 'Female',
    'Mother of.': 'Female',
    'Mother In Law.': 'Female',
    'D/o.': 'Female',
    'W/O.': 'Female'
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'dob') {
      const computedAge = value ? calculateAge(value) : '';
      setFormData((prev) => ({
        ...prev,
        dob: value,
        age: computedAge,
      }));
      return;
    }
    if (name === 'salutation') {
      const mappedGender = salutationToGender[value] || null;
      setFormData((prev) => ({
        ...prev,
        salutation: value,
        ...(mappedGender ? { gender: mappedGender } : {}),
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };
  const normalizeSalutation = (s) => {
    if (!s) return "";
    return s.endsWith(".") ? s : `${s}.`;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingMessage("Submitting Booking details...");
    setLoading(true);

    // if (!formData.firstName || !formData.mobileNo) {
    //   toast.warn("First name and mobile number are required.");
    //   setLoading(false);
    //   return;
    // }

    // if (!/^\d{10}$/.test(formData.mobileNo)) {
    //   toast.warn("Please enter a valid 10-digit mobile number.");
    //   setLoading(false);
    //   return;
    // }

    // if (!formData.selectedDoctor) {
    if (formData.visitType === "appointment") {
      // Appointment requires doctor + slot
      if (!formData.selectedDoctor) {
        toast.error("Please select a doctor before submitting.");
        setLoading(false);
        return;
      } else if (!formData.slotDateTime) {
        toast.error("Please select a slot before submitting.");
        setLoading(false);
        return;
      }
    } else {
      // Walk-in requires only doctor
      if (!formData.selectedDoctor) {
        toast.error("Please select a doctor before submitting.");
        setLoading(false);
        return;
      }
      if (!formData.availableWalkins) {
        toast.error("Walkins not available for the selected doctor.");
        setLoading(false);
        return;
      }
    }


    const consultationFeeLoc = formData?.consultationFee;

    if (
      consultationFeeLoc === null ||
      consultationFeeLoc === undefined ||
      consultationFeeLoc === "" ||
      isNaN(Number(consultationFeeLoc)) ||
      Number(consultationFeeLoc) < 0
    ) {
      toast.error("Consultation fee is invalid. Cannot proceed.");
      setLoading(false);
      return;
    }


    setLoading(true);

    let paymentResponse = null;
    let patientId;
    let isNewPatient = false;

    try {
      let paymentReferenceId;
      patientId = localStorage.getItem("patientId");
      // Register patient OR reuse pre-filled patient data
      if (patientId) {
      } else {
        const registerPayload = {
          salutation: normalizeSalutation(formData.salutation),
          first_name: formData.firstName,
          middle_name: formData.middleName,
          last_name: formData.lastName,
          gender: formData.gender,
          dob: formData.dob,
          age: formData.age,
          mobile_no: verifiedPhoneNumber,
          email: formData.email,
          address: formData.address,
          address1: formData.address1,
          address2: formData.address2,
          address3: formData.address3,
          city: formData.city,
          district: formData.district,
          district_id: formData.district_id,
          state: formData.state,
          state_id: formData.state_id,
          pincode: formData.pincode,
          country: formData.country,
          country_id: formData.country_id,
          marital_status: formData.maritalStatus,
        };

        const regRes = await registerPatient(registerPayload);
        patientId = regRes.patient_id;
        isNewPatient = true;
        setNewPatientRegistered(true);
        const data = await fetchPatientByPhone(verifiedPhoneNumber);
        localStorage.setItem("patients", JSON.stringify(data?.patients || []));
        const selectedPatient = data?.patients?.find(p => p.id === patientId) || null;
        localStorage.setItem("patientId", patientId);
        localStorage.setItem("patient", JSON.stringify(selectedPatient));
        prefillData = selectedPatient;
        setOpCardPatient(selectedPatient);
        onPatientRegistered?.(patientId, selectedPatient);

      }

      // ---------------------------------------------------------------
      // 🔵 HDFC: Build booking payload, save state, redirect (pay_now only)
      // ---------------------------------------------------------------
      const gatewayType = process.env.REACT_APP_PAY_GATEWAY_TYPE || "razorpay";

      if (payGatewayEnabled && formData.paymentMode === "pay_now" && gatewayType === "hdfc") {
        let hdfc_booking_type = formData.visitType === "appointment" ? "A" : "W";
        let hdfc_appointment_type =
          formData.visitType === "appointment" && formData.appointmentType === "telemedicine"
            ? "T"
            : "";

        const now = new Date();
        const today =
          now.getFullYear() +
          "-" +
          String(now.getMonth() + 1).padStart(2, "0") +
          "-" +
          String(now.getDate()).padStart(2, "0");
        const currentTime =
          String(now.getHours()).padStart(2, "0") +
          ":" +
          String(now.getMinutes()).padStart(2, "0") +
          ":" +
          String(now.getSeconds()).padStart(2, "0");
        const slot = formData.slotDateTime;
        const hdfc_booking_date_time = `${slot?.appointmentDate || today} ${
          slot?.startTime ? `${slot.startTime}:00` : currentTime
        }`;

        const hdfcBookPayload = {
          patient_id: String(patientId),
          doctor_id: formData.selectedDoctor,
          shift_id: slot?.shiftId,
          token_number: slot?.tokenNumber,
          booking_type: hdfc_booking_type,
          appointment_type: hdfc_appointment_type,
          amount: consultationFeeLoc,
          slot_id: slot?.slotId || null,
          slot_window: calculateSlotWindow(slot?.startTime, slot?.endTime),
          booking_date_time: hdfc_booking_date_time,
          department_id: formData.selectedDepartment ? String(formData.selectedDepartment) : "0",
          sub_department_id: formData.selectedSubDepartment ? String(formData.selectedSubDepartment) : "0",
          service_depid: "1",
          location_code: process.env.REACT_APP_LOCATION_CODE,
          doctor_charges: String(consultationFeeLoc),
          doctor_name: formData.selectedDoctorName,
        };

        await initiateHDFCPayment({
          createHDFCSessionFn: createHDFCSession,
          amount: consultationFeeLoc,
          patientId,
          bookingPayload: hdfcBookPayload,
          booking_type: hdfc_booking_type,
          consultationFee: consultationFeeLoc,
        });
        return; // Browser redirects to HDFC — execution stops here
      }

      // ---------------------------------------------------------------
      // 🟢 CONDITIONAL PAYMENT OR DIRECT BOOKING
      // ---------------------------------------------------------------
      let paidStatus = 0; // Default to PAID (for non-payment mode)

      if (payGatewayEnabled) {

        if (formData.paymentMode === "pay_now") {
          const razorPayOrderPayload = {
            amount: consultationFeeLoc,
            patient_id: patientId?.toString(),

          }

          const order = await createRazorPayOrder(razorPayOrderPayload);
          // 🔵 Payment Gateway Enabled → Razorpay flow
          paymentResponse = await loadAndOpenRazorpay({
            key: process.env.REACT_APP_RAZORPAY_KEY_ID,
            amount: consultationFeeLoc,
            orderId: order.orderId
          });
          paymentReferenceId = paymentResponse.razorpay_payment_id;
          paidStatus = 1; // Razorpay success
        } else {
          paidStatus = 0; // Pay at counter
        }

      }
      let booking_type = ""
      let appointment_type = ""
      if (formData.visitType === "appointment") {
        booking_type = 'A'
        if (formData.appointmentType === "telemedicine") {
          appointment_type = 'T'
        }


      }
      else {
        booking_type = 'W'
      }
      // ---------------------------------------------------------------
      // 🔵 BOOK SLOT (Common for both payment/no-payment mode)
      // ---------------------------------------------------------------
      const bookPayload = {
        patient_id: String(patientId),
        doctor_id: formData.selectedDoctor,
        shift_id: formData.slotDateTime?.shiftId,
        token_number: formData.slotDateTime?.tokenNumber,
        booking_type: booking_type,
        appointment_type: appointment_type,
        amount: consultationFeeLoc,
        slot_id: formData.slotDateTime?.slotId || null,

        slot_window: calculateSlotWindow(formData.slotDateTime?.startTime, formData.slotDateTime?.endTime),
        booking_date_time: (() => {
          const slot = formData.slotDateTime;

          const now = new Date();

          // LOCAL DATE (Fix for IST / timezone issues)
          const today =
            now.getFullYear() + "-" +
            String(now.getMonth() + 1).padStart(2, "0") + "-" +
            String(now.getDate()).padStart(2, "0");

          // LOCAL TIME HH:MM:SS
          const currentTime =
            String(now.getHours()).padStart(2, "0") + ":" +
            String(now.getMinutes()).padStart(2, "0") + ":" +
            String(now.getSeconds()).padStart(2, "0");

          const date = slot?.appointmentDate || today;
          const time = slot?.startTime ? `${slot.startTime}:00` : currentTime;

          return `${date} ${time}`;
        })(),

        department_id: formData.selectedDepartment ? String(formData.selectedDepartment) : "0",
        sub_department_id: formData.selectedSubDepartment ? String(formData.selectedSubDepartment) : "0",

        service_depid: "1",
        location_code: process.env.REACT_APP_LOCATION_CODE,
        doctor_charges: String(consultationFeeLoc),
        doctor_name: formData.selectedDoctorName,
      };


      const bookRes = await bookSlot(bookPayload);

      // ---------------------------------------------------------------
      // 🔵 PAYMENT UPDATE (marks booking as PAID)
      // ---------------------------------------------------------------
      // 🔵 PAYMENT UPDATE (marks booking as PAID) only if paidStatus === 1
      if (paidStatus === 1) {
        const paymentUpdatePayload = {
          patient_id: String(patientId),
          doctor_id: formData.selectedDoctor,
          booking_type: booking_type,
          paid_amount: consultationFeeLoc,
          reference_id: paymentReferenceId,
          booking_date_time: (() => {
            const slot = formData.slotDateTime;

            const now = new Date();

            const today =
              now.getFullYear() + "-" +
              String(now.getMonth() + 1).padStart(2, "0") + "-" +
              String(now.getDate()).padStart(2, "0");

            const currentTime =
              String(now.getHours()).padStart(2, "0") + ":" +
              String(now.getMinutes()).padStart(2, "0") + ":" +
              String(now.getSeconds()).padStart(2, "0");

            const date = slot?.appointmentDate || today;
            const time = slot?.startTime ? `${slot.startTime}:00` : currentTime;

            return `${date} ${time}`;
          })(),
          next_token: bookRes.token,
          bill_head_id: bookRes.bill_head_id,
          paid_status: paidStatus,
        };

        const paymentRes = await updatePaymentStatus(paymentUpdatePayload);
        toast.success(`${paymentRes.message}`);
      }
      else {
        toast.success(`${bookRes.message}.`);
      }


      // Reset form
      setFormData(prev => ({
        ...prev,
        selectedDepartment: "",
        selectedDoctor: "",
        selectedDoctorName: "",
        slotDateTime: null,
        consultationFee: "",

      }));

      // onSubmitSuccess?.();


      // after successful booking
      if (isNewPatient) {
        setShowOpCard(true);
        setRedirectAfterOpCard(true);
      } else {
        onBookingSuccess?.(); // normal redirect
      }

    } catch (error) {
      console.error("Process failed:", error);
      if (error.code === 'RAZORPAY_CANCELLED' || error.code === 'RAZORPAY_FAILED' || error.code === 'INVALID_AMOUNT') {
        toast.error(error.message || "Payment Failed or Cancelled");
      } else {
        if (paymentResponse) {
          toast.error("Payment successful, but booking failed. Please contact support.");
        } else {
          toast.error(
            error?.response?.data?.message ||
            error?.message ||
            t('patient.error')
          );
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">

      {/* Show loader on top of the form */}
      {loading && <Loader message={loadingMessage} />}
      <div className="max-w-5xl mx-auto p-4 md:p-6
                      text-gray-900 dark:text-gray-100
                      transition-colors duration-300">
        <form id="patient-form" onSubmit={handleSubmit} className="space-y-6">

          <PersonalDetails
            form={formData}
            onShowOpCard={(patient) => {
              setOpCardPatient(patient);
              setShowOpCard(true);
            }}
            onChange={handleChange}
            isPrefilled={Boolean(prefillData)}
            masterLists={masterLists}
          />

          <DoctorDetails form={formData} onChange={handleChange} isExistingPatient={Boolean(prefillData)} />

        </form>


      </div>
      <PatientOpCard
        show={showOpCard}
        selectedPatient={opCardPatient || prefillData}
        hospitalName={hospitalName}
        logo={logo}
        onClose={() => {
          setShowOpCard(false);
          if (redirectAfterOpCard) {
            onBookingSuccess?.();
          }
        }}
      />
    </div>

  );
};

export default PatientForm;
